import { check as breakerCheck, recordFailure, recordSuccess, routeKey } from './breaker.js';
import { getConfig } from './config.js';
import { BreakerOpenError, HttpError, TimeoutError } from './error.js';
import { withRetry } from './retry.js';
import { traceparent } from './trace.js';
function resolveUrl(url, baseUrl) {
    if (url.startsWith('http://') || url.startsWith('https://'))
        return url;
    return baseUrl + url;
}
function resolveRetry(base, override) {
    if (override === false)
        return { ...base, maxAttempts: 1 };
    if (override === undefined || override === true)
        return base;
    return { ...base, ...override };
}
function mergeSignals(a, b) {
    if (a.aborted)
        return a;
    if (b.aborted)
        return b;
    const ac = new AbortController();
    const onAbortA = () => ac.abort(a.reason);
    const onAbortB = () => ac.abort(b.reason);
    a.addEventListener('abort', onAbortA, { once: true });
    b.addEventListener('abort', onAbortB, { once: true });
    return ac.signal;
}
async function sendOnce(url, options, defaultTimeoutMs) {
    const config = getConfig();
    const timeoutMs = options.timeoutMs ?? defaultTimeoutMs;
    let timedOut = false;
    const timeoutAc = new AbortController();
    const timer = setTimeout(() => {
        timedOut = true;
        timeoutAc.abort();
    }, timeoutMs);
    const signal = options.signal ? mergeSignals(options.signal, timeoutAc.signal) : timeoutAc.signal;
    const headers = new Headers(options.headers);
    if (!options.skipAuth) {
        const token = config.getAccessToken();
        if (token)
            headers.set('Authorization', `Bearer ${token}`);
    }
    const tp = traceparent();
    if (tp && !headers.has('traceparent'))
        headers.set('traceparent', tp);
    const { skipAuth: _skip, timeoutMs: _t, retry: _r, signal: _s, ...rest } = options;
    void _skip;
    void _t;
    void _r;
    void _s;
    try {
        return await fetch(url, { ...rest, headers, signal });
    }
    catch (err) {
        if (timedOut)
            throw new TimeoutError(timeoutMs);
        throw err;
    }
    finally {
        clearTimeout(timer);
    }
}
async function doRequest(url, options, isAuthRetry) {
    const config = getConfig();
    const fullUrl = resolveUrl(url, config.baseUrl);
    const route = routeKey(fullUrl, config.breaker.routeKeySegments);
    breakerCheck(route, config.breaker);
    const retry = resolveRetry(config.retry, options.retry);
    let response;
    try {
        response = await withRetry(() => sendOnce(fullUrl, options, config.timeoutMs), retry);
    }
    catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError') &&
            !(err instanceof BreakerOpenError)) {
            recordFailure(route, config.breaker);
        }
        throw err;
    }
    if (response.status === 401 && !options.skipAuth && !isAuthRetry) {
        const refreshed = await config.onUnauthorized();
        if (refreshed) {
            return doRequest(fullUrl, options, true);
        }
    }
    if (response.status >= 500) {
        recordFailure(route, config.breaker);
    }
    else {
        recordSuccess(route);
    }
    return response;
}
/**
 * Core request entrypoint. Returns the raw `Response`; the caller reads the
 * body. Subject to: Bearer attach, traceparent inject, timeout, retry/backoff,
 * 401-refresh-retry, per-route circuit breaker.
 */
export function httpClient(url, options = {}) {
    return doRequest(url, options, false);
}
async function parseJson(res) {
    const text = await res.text();
    let body = null;
    if (text.length > 0) {
        try {
            body = JSON.parse(text);
        }
        catch {
            body = text;
        }
    }
    if (!res.ok) {
        throw new HttpError(res.status, `${res.status} ${res.statusText}`.trim(), body);
    }
    return body;
}
/** Issue a `GET`, parse JSON, throw `HttpError` on non-2xx. */
export async function getJson(url, options) {
    const headers = new Headers(options?.headers);
    if (!headers.has('Accept'))
        headers.set('Accept', 'application/json');
    const res = await httpClient(url, { ...options, method: 'GET', headers });
    return parseJson(res);
}
/**
 * Issue a `POST` with a JSON body, parse JSON response, throw `HttpError`
 * on non-2xx. Sets `Content-Type: application/json` and
 * `Accept: application/json` unless the caller overrides them.
 */
export async function postJson(url, body, options) {
    const headers = new Headers(options?.headers);
    if (!headers.has('Content-Type'))
        headers.set('Content-Type', 'application/json');
    if (!headers.has('Accept'))
        headers.set('Accept', 'application/json');
    const res = await httpClient(url, {
        ...options,
        method: 'POST',
        body: JSON.stringify(body),
        headers,
    });
    return parseJson(res);
}
//# sourceMappingURL=client.js.map